import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { InitialConfigType, LexicalComposer } from '@lexical/react/LexicalComposer';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import * as Sentry from '@sentry/react';
import { memo, useEffect } from 'react';
import buildTheme from './config/buildTheme';
import { createTransformers } from './config/transformers';
import nodes from './config/nodes';
import { $setEditorValue } from './utils';
import './markdown-editor.css';

type MarkdownViewProps = {
  value: string;
};

export const MarkdownView = memo(function MarkdownView({ value }: MarkdownViewProps) {
  const initialConfig: InitialConfigType = {
    namespace: 'MarkdownView',
    theme: buildTheme(),
    onError: (error: Error) => Sentry.captureException(error),
    editable: false,
    nodes,
  };

  return (
    <LexicalComposer initialConfig={initialConfig}>
      <RichTextPlugin
        contentEditable={<ContentEditable style={{ outline: 'none' }} />}
        placeholder={null}
        ErrorBoundary={LexicalErrorBoundary}
      />
      <MarkdownSyncPlugin value={value} />
    </LexicalComposer>
  );
});

function MarkdownSyncPlugin({ value }: { value: string }) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    editor.update(() => {
      const transformers = createTransformers(editor);
      $setEditorValue(value, transformers);
    });
  }, [editor, value]);

  return null;
}
